import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    const isHls = (el) => {
      const t = (el.getAttribute('type') || '').toLowerCase();
      const s = (el.dataset.videoSource || '').toLowerCase();
      return t.indexOf('mpegurl') !== -1 || s.indexOf('.m3u8') !== -1;
    };
    const ua = navigator.userAgent;
    const nativeHls = /safari/i.test(ua) && !/chrome|chromium|crios|android|edg|fxios/i.test(ua);

    const all = Array.from(videoSources);
    const plain = all.filter((s) => !isHls(s));
    const hls = all.filter(isHls);
    const keep = plain.length ? plain.concat(nativeHls ? hls : []) : all;

    for (const source of all) {
      if (keep.indexOf(source) === -1) {
        source.remove();
        continue;
      }
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    for (const source of keep) videoElement.appendChild(source);

    videoElement.muted = true;
    videoElement.defaultMuted = true;
    videoElement.setAttribute('muted', '');
    videoElement.setAttribute('playsinline', '');
    videoElement.setAttribute('autoplay', '');
    videoElement.load();

    const tryPlay = () => {
      const p = videoElement.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };

    tryPlay();

    videoElement.addEventListener('loadeddata', tryPlay);
    videoElement.addEventListener('canplay', tryPlay);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) tryPlay();
    });
    const resume = () => {
      tryPlay();
      document.removeEventListener('touchstart', resume);
      document.removeEventListener('click', resume);
    };
    document.addEventListener('touchstart', resume, { once: true, passive: true });
    document.addEventListener('click', resume, { once: true });
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
